import React, { useMemo, useState } from "react";
import {
  Check,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from "lucide-react";
import Navbar from "src/Components/Header/Navbar";
import Footer from "src/Components/Footer/Footer";
import { useTranslation } from "react-i18next";
import clubPlanImage from "src/assets/images/smst.jpg";
import clubProPlanImage from "src/assets/images/field.jpg";
import clubElitePlanImage from "src/assets/images/bigst.jpg";
import playerPlanImage from "src/assets/images/player/Free.jpg";
import playerProPlanImage from "src/assets/images/player/Premium.jpg";
import coachPlanImage from "src/assets/images/coach/Free.jpg";
import coachProPlanImage from "src/assets/images/coach/Premium.jpg";

type Audience = "player" | "coach";

type Plan = {
  title: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  note?: string;
  billingBadge?: string;
  badge?: string;
  image: string;
};

type AudiencePlanGroup = {
  title: string;
  plans: Plan[];
};

type PlanCopy = {
  title: string;
  description: string;
  features: string[];
};

const formatPrice = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);

export const Pricing = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-US" : "es-ES";
  const [activeAudience, setActiveAudience] = useState<Audience>("player");

  const monthLabel = t("pricing.labels.month");
  const freeLabel = t("pricing.labels.free");
  const clubCopy = t("pricing.planGroups.club.plans", {
    returnObjects: true,
  }) as PlanCopy[];
  const playerFreeFeatures = t("pricing.planGroups.player.free.features", {
    returnObjects: true,
  }) as string[];
  const playerProFeatures = t("pricing.planGroups.player.pro.features", {
    returnObjects: true,
  }) as string[];
  const coachFreeFeatures = t("pricing.planGroups.coach.free.features", {
    returnObjects: true,
  }) as string[];
  const coachProFeatures = t("pricing.planGroups.coach.pro.features", {
    returnObjects: true,
  }) as string[];

  const clubPlans: Plan[] = useMemo(
    () =>
      clubCopy.map((plan, index) => ({
        ...plan,
        price: `${formatPrice([30, 60, 90][index], locale)} €`,
        period: `/${monthLabel}`,
        badge: index === 1 ? t("pricing.labels.popular") : undefined,
        image: [clubPlanImage, clubProPlanImage, clubElitePlanImage][index],
      })),
    [clubCopy, locale, monthLabel, t]
  );

  const audienceGroups: Record<Audience, AudiencePlanGroup> = useMemo(
    () => ({
      player: {
        title: t("pricing.planGroups.player.title"),
        plans: [
          {
            title: freeLabel,
            price: freeLabel,
            period: "",
            description: t("pricing.planGroups.player.free.description"),
            features: playerFreeFeatures,
            image: playerPlanImage,
          },
          {
            title: t("pricing.planGroups.player.pro.title"),
            price: `${formatPrice(2.5, locale)} €`,
            period: `/${monthLabel}`,
            description: t("pricing.planGroups.player.pro.description"),
            features: playerProFeatures,
            note: t("pricing.planGroups.player.pro.annualNote"),
            billingBadge: t("pricing.labels.annualPlan"),
            image: playerProPlanImage,
          },
        ],
      },
      coach: {
        title: t("pricing.planGroups.coach.title"),
        plans: [
          {
            title: freeLabel,
            price: freeLabel,
            period: "",
            description: t("pricing.planGroups.coach.free.description"),
            features: coachFreeFeatures,
            image: coachPlanImage,
          },
          {
            title: t("pricing.planGroups.coach.pro.title"),
            price: `${formatPrice(5.95, locale)} €`,
            period: `/${monthLabel}`,
            description: t("pricing.planGroups.coach.pro.description"),
            features: coachProFeatures,
            image: coachProPlanImage,
          },
        ],
      },
    }),
    [
      coachFreeFeatures,
      coachProFeatures,
      freeLabel,
      locale,
      monthLabel,
      playerFreeFeatures,
      playerProFeatures,
      t,
    ]
  );

  const renderPlanCard = (plan: Plan) => (
    <article
      key={plan.title}
      className="group relative flex h-full flex-col overflow-visible rounded-2xl border border-white/15 bg-white/[0.08] shadow-2xl shadow-black/20 backdrop-blur-xl"
    >
      {plan.badge && (
        <div className="absolute left-1/2 top-0 z-30 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-2 text-sm font-bold text-white shadow-md">
          {plan.badge}
        </div>
      )}

      <div className="relative h-44 overflow-hidden rounded-t-2xl">
        <img
          src={plan.image}
          alt={plan.title}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/25 to-transparent" />
      </div>

      <div className="flex flex-1 flex-col p-6">
        <div className="mb-5">
          <h3 className="text-2xl font-black text-white">{plan.title}</h3>
          <p className="mt-2 min-h-[3rem] text-sm leading-6 text-slate-300">
            {plan.description}
          </p>
        </div>

        <div className="mb-6 flex items-end gap-2">
          <span className="text-4xl font-black text-white">{plan.price}</span>
          {plan.period && (
            <span className="pb-1 text-sm font-semibold text-slate-400">{plan.period}</span>
          )}
        </div>

        {(plan.billingBadge || plan.note) && (
          <div className="mb-6 rounded-xl border border-cyan-300/20 bg-cyan-300/10 px-3 py-3 text-sm leading-5 text-cyan-100">
            {plan.billingBadge && (
              <span className="mb-1 block text-xs font-black uppercase tracking-[0.14em] text-cyan-200">
                {plan.billingBadge}
              </span>
            )}
            {plan.note && <span className="font-semibold text-slate-200">{plan.note}</span>}
          </div>
        )}

        <ul className="space-y-3">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3 text-sm leading-6 text-slate-200">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300">
                <Check className="h-3.5 w-3.5" />
              </span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );

  const activeGroup = audienceGroups[activeAudience];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />

      <main className="relative overflow-hidden pt-36">
        <section className="relative px-4 pb-14">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.25),transparent_34%),radial-gradient(circle_at_top_right,rgba(217,70,239,0.22),transparent_36%),linear-gradient(180deg,#020617_0%,#0f172a_52%,#020617_100%)]" />

          <div className="mx-auto max-w-6xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-cyan-100 backdrop-blur">
              <Sparkles className="h-4 w-4" />
              {t("pricing.hero.badge")}
            </div>

            <h1 className="mx-auto max-w-4xl text-4xl font-black leading-tight text-white md:text-6xl">
              {t("pricing.hero.title")}
            </h1>

            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-300">
              {t("pricing.hero.description")}
            </p>
          </div>
        </section>

        <section className="px-4 py-10">
          <div className="mx-auto max-w-6xl">
            <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-cyan-300">
                  <ShieldCheck className="h-4 w-4" />
                  {t("pricing.planGroups.club.title")}
                </div>
                <h2 className="text-3xl font-black text-white md:text-4xl">
                  {t("pricing.planGroups.club.title")}
                </h2>
                <p className="mt-3 max-w-2xl text-base leading-7 text-slate-300">
                  {t("pricing.planGroups.club.subtitle")}
                </p>
              </div>
            </div>

            <div className="grid gap-6 pt-4 lg:grid-cols-3">{clubPlans.map(renderPlanCard)}</div>
          </div>
        </section>

        <section className="px-4 py-14">
          <div className="mx-auto max-w-6xl">
            <div className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-end">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-fuchsia-300">
                  <UsersRound className="h-4 w-4" />
                  {t("pricing.planGroups.people.title")}
                </div>
                <h2 className="text-3xl font-black text-white md:text-4xl">
                  {activeGroup.title}
                </h2>
                <p className="mt-3 max-w-2xl text-base leading-7 text-slate-300">
                  {t("pricing.planGroups.people.subtitle")}
                </p>
              </div>

              <div className="inline-flex w-full rounded-2xl border border-white/15 bg-white/10 p-1 backdrop-blur md:w-auto">
                {(["player", "coach"] as Audience[]).map((audience) => (
                  <button
                    key={audience}
                    type="button"
                    onClick={() => setActiveAudience(audience)}
                    className={`flex-1 rounded-xl px-5 py-3 text-sm font-black transition md:flex-none ${
                      activeAudience === audience
                        ? "bg-white text-slate-950 shadow-lg"
                        : "text-slate-300 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    {audienceGroups[audience].title}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-6 pt-4 md:grid-cols-2">{activeGroup.plans.map(renderPlanCard)}</div>
          </div>
        </section>

        <section className="px-4 pb-20">
          <div className="mx-auto max-w-6xl rounded-2xl border border-white/15 bg-white/[0.07] p-6 text-center text-sm font-semibold leading-7 text-slate-300 backdrop-blur">
            {t("pricing.informational")}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};
